import { createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import particleVowels from '../../utils/particleVowels';

import socket from '../../utils/socket';

const initialState = {
  isLoading: false,
  error: false,
  // pdf url to access Laravel API File
  pdfUrl: null
};

const slice = createSlice({
  name: 'prints',
  initialState,
  reducers: {
    // LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
      console.error(state.error);
    },

    getPdfFromUrl(state, action) {
      const { url } = action.payload;
      state.isLoading = false;
      state.pdfUrl = url;
    },

    // Pour éviter l'apparition non voulue de données précédentes => reset
    cleanPdfUrl(state) {
      state.pdfUrl = initialState.pdfUrl;
      state.isLoading = initialState.isLoading;
    }
  }
});

// Reducer
export default slice.reducer;

// actions
export const { setOpenServices, setOpenSignatures, cleanPdfUrl } = slice.actions;

const sanitizeSchedulesResource = (date, input, company, resource, contract) => {
  const data = [];
  const siteNames = [];
  let profilLength = 0;
  let hasMarkedVacation = false;
  input.forEach((element) => {
    let copyShifts = [];
    let shifts = [];
    let lineName = '';
    let isSite = false;
    const profiles = [];
    const arrayOfProfilesId = [];
    const timesStart = [];
    const timesEnd = [];
    const breakTimes = [];

    if (!siteNames.includes(element.site_name) && element.site_name && element.site_name.length > 0) {
      lineName = element.site_name;
      isSite = true;
      siteNames.push(lineName);
      shifts = input.filter((el) => el.site_name === lineName);
      shifts.forEach((shift) => {
        if (shift.isMarked || shift.isBonus) {
          hasMarkedVacation = true;
        }
        let code = '';
        let breakEnd = null;
        if (shift.break_datetime_start && shift.break_duration) {
          breakEnd = moment(shift.break_datetime_start, 'HH:mm:ss')
            .add(shift.break_duration, 'minutes')
            .format('HH:mm');
        }

        const exists =
          arrayOfProfilesId.includes(shift.profile_id) ||
          timesStart.includes(shift.time_start) ||
          timesEnd.includes(shift.time_end) ||
          breakTimes.includes(shift.break_duration);

        if (!exists) {
          profilLength += 1;
          code = shift.is_night ? `N${profilLength}` : `J${profilLength}`;
          arrayOfProfilesId.push(shift.profile_id);
          timesEnd.push(shift.time_end);
          timesStart.push(shift.time_start);
          breakTimes.push(shift.break_duration);
          const profile = {
            id: shift.profile_id,
            code,
            activity_code: shift.activity_code,
            activity_name: shift.activity_name,
            name: shift.profile_name,
            times: [shift.time_start, shift.time_end],
            breakDuration: shift.break_duration,
            breakStart: shift.break_datetime_start ? shift.break_datetime_start.slice(0, 5) : null,
            breakEnd,
            instructions: shift.profile_instruction,
            prestation_name: shift.prestation_name,
            show_break_hours: shift.show_break_hours
          };
          profiles.push(profile);
        } else {
          code = shift.is_night ? `N${profilLength}` : `J${profilLength}`;
        }

        const copyShift = { ...shift };
        copyShift.activity_code = code;
        copyShifts.push(copyShift);
      });
    } else if (
      !element.site_name &&
      element.unavailability_type_name &&
      !element.is_demand &&
      !siteNames.includes(element.unavailability_type_name)
    ) {
      lineName = element.unavailability_type_name;
      siteNames.push(lineName);
      copyShifts = input.filter((el) => el.unavailability_type_name === lineName && !el.is_demand);
    }
    if (lineName.length) {
      const row = {
        name: lineName,
        sector: element.sector_site_name,
        isSite,
        profiles,
        address_full: element.site_address_full || null,
        instructions: element.site_instructions ? element.site_instructions : null,
        access: element.site_access ? element.site_access : null,
        phone: element.site_phone ? element.site_phone : null,
        phone_urgency: element.site_phone_urgency ? element.site_phone_urgency : null,
        shifts: copyShifts
      };
      data.push(row);
    }
  });

  const objectToSend = {
    parameters: socket.env.parameters,
    company,
    settings: socket.env.settings,
    planningType: 'resource',
    dayInMonth: moment(date, 'MMMM YYYY').daysInMonth(),
    date: moment(date, 'MMMM YYYY').format('YYYY-MM'),
    today: moment().format('DD/MM/YYYY'),
    timeEdition: moment().format('HH:mm'),
    dateMonth: moment(date, 'MMMM YYYY').format('MMMM'),
    dateYear: moment(date, 'MMMM YYYY').format('YYYY'),
    resource_fullname: resource.fullname,
    matriculation: contract.matriculation,
    typePlanning: 'PLANNING_AGENT/PDP',
    resource: { ...resource, hasMarkedVacation },
    data
  };

  return objectToSend;
};

const sanitizeSchedulesSite = (date, site, filtersScheduleSite) => {
  const objectToSend = {
    parameters: socket.env.parameters,
    site,
    filtersScheduleSite,
    settings: socket.env.settings,
    planningType: 'site',
    dayInMonth: moment(date, 'MMMM YYYY').daysInMonth(),
    date: moment(date, 'MMMM YYYY').format('YYYY-MM'),
    today: moment().format('DD/MM/YYYY'),
    timeEdition: moment().format('HH:mm'),
    dateMonth: moment(date, 'MMMM YYYY').format('MMMM'),
    dateYear: moment(date, 'MMMM YYYY').format('YYYY'),
    typePlanning: 'PLANNING_SITE/PDP'
  };

  return objectToSend;
};

export function getPdfFromUrl({
  date,
  view,
  isCalendar,
  siteId,
  month,
  datas = null,
  filters = null,
  hasServices = 0,
  isSentByEmail = 0
}) {
  return async (dispatch, getState) => {
    const { resource, contract } = getState().resources;
    const { company } = getState().companies;
    const { siteSelected } = getState().sites;
    dispatch(slice.actions.startLoading());
    try {
      let formatDate = 'DD/MM/YYYY';
      let title = '';
      let moreParams = {};
      switch (view) {
        case 'services':
          datas = { filters };
          moreParams = { date, hasServices };
          title = `Liste des prises de service du ${moment(date).format(formatDate)}`;
          break;
        case 'signatures':
          datas = { filters };
          moreParams = { date, hasServices };
          title = `Feuilles d'émargements du ${moment(date).format(formatDate)}`;
          break;
        case 'schedulesResource':
          datas = sanitizeSchedulesResource(date, datas, company, resource, contract);
          formatDate = 'MMMM YYYY';
          title = `Planning de l'agent ${datas.resource_fullname} du mois ${particleVowels(date)}${date}`;
          break;
        case 'schedulesSite':
          datas = sanitizeSchedulesSite(date, siteSelected, filters);
          title = `${siteSelected.id === 0 ? '' : 'Planning site '}${siteSelected.name} ${filters.isSiteSchedule ? 'par sites' : 'par agents'} du mois ${particleVowels(date)}${date}`;
          break;
        default:
          break;
      }

      const params = {
        isSentByEmail,
        title,
        view,
        datas,
        isCalendar,
        siteId,
        month,
        ...moreParams
      };

      // Génération PDF peut-être longue, on n'attend pas la réponse
      if (isSentByEmail) {
        socket.post('/print', params).catch((error) => dispatch(slice.actions.hasError(error)));
      } else {
        const result = await socket.post('/print', params);
        dispatch(slice.actions.getPdfFromUrl(result));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
